import React from "react";
import { ArrowRight, Phone, Heart, Home, ShieldCheck, Users, Sparkles } from "lucide-react";

const head = { fontFamily: "'Outfit', sans-serif" };
const body = { fontFamily: "'Manrope', sans-serif" };

const pillars = [
  { icon: Users, title: "A guard who knows you", desc: "The same friendly face every day — they learn your residents, vendors and rhythms." },
  { icon: Home, title: "Calm, not surveilled", desc: "Security that feels like a watchful neighbor, never an intrusion on daily life." },
  { icon: ShieldCheck, title: "Always looking out", desc: "Gentle, proactive monitoring that quietly stops problems before they start." },
];

const Option2Peace = () => {
  return (
    <div style={body} className="bg-[#FAF9F6] text-[#2C3E50]">
      {/* Nav */}
      <header>
        <div className="max-w-6xl mx-auto px-8 h-24 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#C0392B] flex items-center justify-center text-white"><Heart className="w-4 h-4" /></div>
            <span style={head} className="text-2xl font-extrabold">Redline</span>
          </div>
          <nav className="hidden lg:flex items-center gap-9 text-[15px] font-medium text-[#2C3E50]/70">
            <a href="#" className="hover:text-[#C0392B]">Our Guards</a>
            <a href="#" className="hover:text-[#C0392B]">Communities</a>
            <a href="#" className="hover:text-[#C0392B]">Stories</a>
            <a href="#" className="hover:text-[#C0392B]">Partnership</a>
          </nav>
          <div className="flex items-center gap-5">
            <span className="hidden md:flex items-center gap-2 text-sm font-semibold"><Phone className="w-4 h-4 text-[#C0392B]" />(512) 363-6584</span>
            <button className="bg-[#C0392B] text-white px-6 py-3 text-sm font-semibold rounded-full shadow-sm hover:shadow-md transition-shadow">Get Started</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-8 pt-10 pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 bg-[#EAE6DF] rounded-full px-4 py-2 mb-8 text-sm font-semibold text-[#C0392B]">
              <Sparkles className="w-4 h-4" /> Peace of mind, delivered daily
            </div>
            <h1 style={head} className="text-5xl xl:text-6xl font-extrabold leading-[1.05] mb-7">
              Your neighborhood,<br />safe and <span className="text-[#C0392B]">sound.</span>
            </h1>
            <p className="text-lg text-[#2C3E50]/70 max-w-lg mb-9 leading-relaxed">
              A dedicated virtual guard watches over your HOA community like a trusted neighbor — so families relax, kids play, and packages stay put. Warm, human security for the places you call home.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <button className="bg-[#C0392B] text-white px-8 py-4 rounded-full font-semibold shadow-sm hover:shadow-lg transition-shadow flex items-center gap-2">Schedule a friendly chat <ArrowRight className="w-4 h-4" /></button>
              <button className="px-6 py-4 rounded-full font-semibold text-[#2C3E50] hover:bg-[#EAE6DF] transition-colors">See how it works</button>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <img src="https://images.pexels.com/photos/1073133/pexels-photo-1073133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Family neighborhood" className="w-full h-[480px] object-cover rounded-[2rem] shadow-xl" />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-lg p-5 flex items-center gap-4 max-w-xs">
              <div className="w-12 h-12 rounded-full bg-[#C0392B]/10 flex items-center justify-center"><ShieldCheck className="w-6 h-6 text-[#C0392B]" /></div>
              <div>
                <p style={head} className="font-bold text-[#2C3E50]">Mail theft: eliminated</p>
                <p className="text-xs text-[#2C3E50]/60">Riverside Gate · 180 homes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 style={head} className="text-4xl font-extrabold mb-4">Security that feels like home</h2>
          <p className="text-[#2C3E50]/65 text-lg">We protect the moments that matter, without ever making your community feel like a fortress.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {pillars.map((p) => (
            <div key={p.title} className="bg-white rounded-3xl p-9 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-2xl bg-[#C0392B]/10 flex items-center justify-center mb-6"><p.icon className="w-7 h-7 text-[#C0392B]" /></div>
              <h3 style={head} className="text-xl font-bold mb-3">{p.title}</h3>
              <p className="text-[#2C3E50]/65 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial band */}
      <section className="bg-[#EAE6DF]">
        <div className="max-w-4xl mx-auto px-8 py-20 text-center">
          <div className="flex justify-center gap-1 mb-6">{[0,1,2,3,4].map(i => <Sparkles key={i} className="w-5 h-5 text-[#C0392B] fill-[#C0392B]" />)}</div>
          <blockquote style={head} className="text-2xl xl:text-3xl font-medium leading-snug text-[#2C3E50] mb-8">
            “Our guard knows which residents have visitors and immediately spots anything unusual. Residents feel safe and known.”
          </blockquote>
          <p className="font-semibold">Jennifer Martinez</p>
          <p className="text-sm text-[#2C3E50]/60">HOA Vice President · Hill Country Condos</p>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-8 py-24">
        <div className="bg-[#C0392B] rounded-[2.5rem] px-10 py-16 text-center text-white">
          <h2 style={head} className="text-4xl xl:text-5xl font-extrabold mb-5">Let's keep your community safe</h2>
          <p className="text-white/80 max-w-xl mx-auto mb-9 text-lg">Friendly, no-pressure consultation. We'll show you exactly how a dedicated guard fits your neighborhood.</p>
          <button className="bg-white text-[#C0392B] px-10 py-5 rounded-full font-bold hover:bg-[#FAF9F6] transition-colors inline-flex items-center gap-2">Request your consultation <ArrowRight className="w-5 h-5" /></button>
        </div>
      </section>
    </div>
  );
};

export default Option2Peace;
